<template>
  <teleport to="body">
    <!-- Simple confirmation modal for draft hires -->
    <ConfirmationModal
      v-if="isDraftHire && hire"
      :show="open"
      :title="$t('You’re about to cancel this hire')"
      :description="$t('Cancelling a hire permanently deletes all data connected to it.')"
      :cancel-text="$t('Go back')"
      :confirm-text="$t('Cancel hire')"
      :loading="cancelLoading"
      @cancel="emit('close')"
      @confirm="handleCancel"
    />

    <!-- Detailed modal for other hire statuses -->
    <WdsModal
      v-else-if="hire"
      :show="open"
      md
      :title="$t('Cancel hire with :name', { name: workerName })"
      @cancel="emit('close')"
    >
      <div v-if="loading" class="tw-w-full tw-h-full tw-flex tw-items-center tw-justify-center">
        <WdsSpinner indeterminate />
      </div>

      <WdsStack v-else direction-column gap-md>
        <WdsAlert
          :title="$t('Cancelling a hire permanently deletes all data connected to it.')"
          warning
          icon
        />

        <WdsText>
          {{ $t('The hire may be cancelled for the following reasons:') }}
        </WdsText>

        <ol class="tw-list-disc tw-list-inside">
          <li>{{ $t('Accidental acceptance of the contract') }}</li>
          <li>{{ $t('Mutual agreement to terminate by the parties') }}</li>
          <li>{{ $t('When cancellation is mandated or permitted by law') }}</li>
        </ol>

        <WdsText secondary>
          <span>
            {{
              hire?.recruiterManagesWorkers
                ? $t(
                    'Before cancelling the contract, please review it carefully to understand the implications. Cancellation may not release you from legal or financial responsibilities. For specific advice, consult your legal advisors. If you’re in a dispute with the Staffing Agency, we recommend contacting Worksome Customer Support before proceeding.',
                  )
                : $t(
                    'Before cancelling the contract, please review it carefully to understand the implications. Cancellation may not release you from legal or financial responsibilities. For specific advice, consult your legal advisors. If you’re in a dispute with the worker, we recommend contacting Worksome Customer Support before proceeding.',
                  )
            }}
          </span>
        </WdsText>
        <WdsText secondary>
          {{ $t('You can find details on our dispute resolution process ') }}
          <a
            href="https://www.worksome.com/legal-center/dispute-resolution-policy#legal-document"
            target="_blank"
            class="tw-underline"
          >
            {{ $t('here') }}.
          </a>
        </WdsText>

        <WdsStack direction-column gap-sm>
          <WdsText>
            <template v-if="hire?.recruiterManagesWorkers">
              {{ $t('Write a message to the staffing agency here') }}
            </template>
            <template v-else>
              {{ $t('Write a message to the worker here') }}
            </template>
          </WdsText>

          <WdsInputArea
            v-model="message"
            :placeholder="
              $t(
                'Provide additional context if needed. Your message will be visible in the conversation, so ensure it is accurate, appropriate, and compliant with legal obligations.',
              )
            "
            height="100px"
            required
            :error="validationErrors?.['input.message']"
          />
        </WdsStack>
      </WdsStack>

      <template #footer>
        <WdsStack v-if="!loading" justify-space-between gap-sm>
          <WdsButton @click="emit('close')">
            {{ $t('Go back') }}
          </WdsButton>

          <WdsButton primary :loading="cancelLoading" :disabled="!canCancel" @click="handleCancel">
            {{ $t('Cancel contract') }}
          </WdsButton>
        </WdsStack>
      </template>
    </WdsModal>
  </teleport>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import { useQuery, useMutation } from '@vue/apollo-composable'
import {
  WdsButton,
  WdsModal,
  WdsSpinner,
  WdsStack,
  WdsText,
  WdsInputArea,
  WdsAlert,
} from '@worksome/wds-components'

import { graphql, useFragment } from '@/gql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'
import { Notification, NetworkError } from '@/utils/notification'
import useApolloError from '@/composables/useApolloError'
import ConfirmationModal from '@/components/Common/ConfirmationModal.vue'
import queriesToRefetch from '@/components/Hire/queriesToRefetch'
import { HireActiveStatus } from '@/gql/graphql'

const props = withDefaults(
  defineProps<{
    open?: boolean
    hireId: string
  }>(),
  {
    open: false,
  },
)

const emit = defineEmits(['close', 'save'])

// Reactive variables for form data
const message = ref<string>('')

const HireCancelFragment = graphql(`
  fragment HireCancelFields on Hire {
    id
    activeStatus
    recruiterManagesWorkers
    job {
      id
      name
      isAvailable
    }
    worker {
      id
      name
    }
    recruiter {
      id
      name
    }
  }
`)

const GET_HIRE_FOR_CANCEL = graphql(`
  query GetHireForCancel($id: ID!) {
    hire(id: $id) {
      ...HireCancelFields
    }
  }
`)

const CANCEL_HIRE = graphql(`
  mutation CancelHire($input: CancelHireInput!) {
    cancelHire(input: $input) {
      ...HireCancelFields
    }
  }
`)

const { result, loading, refetch } = useQuery(GET_HIRE_FOR_CANCEL, () => ({ id: props.hireId }), {
  clientId: 'publicApi',
})

const {
  mutate: cancelHire,
  loading: cancelLoading,
  error: cancelError,
} = useMutation(CANCEL_HIRE, {
  clientId: 'publicApi',
  refetchQueries: queriesToRefetch,
})

const { validationErrors } = useApolloError(cancelError)

const hire = computed(() => {
  if (!result.value?.hire) return null
  return useFragment(HireCancelFragment, result.value.hire)
})

const isDraftHire = computed(() => {
  if (!hire.value) return false
  return hire.value.activeStatus === HireActiveStatus.Draft
})

const workerName = computed(() => {
  if (!hire.value) return $t('the freelancer')
  if (hire.value.recruiterManagesWorkers) {
    return hire.value.recruiter?.name || $t('the Staffing Agency')
  }
  return hire.value.worker?.name || $t('the freelancer')
})

const canCancel = computed(() => message.value.trim().length > 0)

const handleCancel = async () => {
  if (!isDraftHire.value && !canCancel.value) return

  try {
    await cancelHire({
      input: {
        hire: props.hireId,
        message: message.value.trim() ?? '',
      },
    })

    new Notification({
      msg: $t('The contract for the job was cancelled.'),
    }).dispatch()

    window.location.reload()
  } catch (error: any) {
    const errorMessage =
      validationErrors.value?.['input.hire'] ||
      validationErrors.value?.['input.message'] ||
      $t('The contract could not be cancelled')

    new NetworkError({
      msg: errorMessage,
      ex: error,
    }).dispatch()
  }
}
</script>
